import React from 'react';
import { useSelector } from 'react-redux';
import { ProductionBatchStatistics } from '@interfaces/search';
import { selectSingleBatch } from '@reducers/batch';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen, BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { getLinkingModalConfig, injectConditionalObject, ncFileStatuses, Popup, withERP } from '@helpers';
import { selectIsWithERP } from '@reducers/membership';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen, JobLevelSubScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { Machine } from '@interfaces/machines';
import { selectSelectedItem } from '@reducers/machines';
import { fullWidth, scale } from '@components/frame';
import ProductionInformation from '@components/productionInformation';
import NCFileType from '@components/common/NCFileType';
import { TopBlockVariant } from '@v2/pages/machines/modules/now/components/Overview/components/TopBlock/types';
import { useBatchLoaded } from '@v2/pages/machines/modules/now/components/Overview/hooks/useBatchLoaded';

const useManageTopBlock = () => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const machine: Machine = useSelector(selectSelectedItem);
  const batch: ProductionBatchStatistics = useSelector(selectSingleBatch);
  const isWithERP = useSelector(selectIsWithERP);
  const { links } = batch || {};

  const isBatchAvailable = machine?.machineStatus?.productionBatchId != null;
  const batchLoaded = useBatchLoaded();

  const ncFileName = machine?.machineStatus?.ncFile;
  const ncFileStatus = machine?.machineStatus?.ncFileStatus;
  const ncFileChange = ncFileStatus === ncFileStatuses.Change;
  const ncFileIgnored = ncFileStatus === ncFileStatuses.Ignored;
  const ncFileUnknown = ncFileStatus === ncFileStatuses.Unknown;
  const ncFileSetup = ncFileStatus === ncFileStatuses.Setup;

  const shouldDisplayNCFileNameForLinkedUnknownNCFileBlock = ncFileUnknown;
  const changeOrIgnoredNCFile = ncFileChange || ncFileIgnored;
  const withoutPressHandlingOnTopBlock = changeOrIgnoredNCFile;

  const variant = changeOrIgnoredNCFile
    ? TopBlockVariant.CHANGE_OR_IGNORED
    : ncFileSetup
      ? TopBlockVariant.SETUP
      : TopBlockVariant.LINKED_OR_UNKNOWN;

  const batchNavigation = ({ selectedImageKey }: { selectedImageKey: string | null }) => {
    if (!batchLoaded) return;

    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: selectedImageKey ? BatchLevelSubScreen.DOCUMENTS : BatchLevelSubScreen.GENERAL,
      params: {
        perspective: Perspective.BATCH,
        batchId: batch?.productionBatchId,
        batchNumber: batch?.batchNumber,
        ncFileStatus: batch?.ncFileStatus,
        ...injectConditionalObject(!!selectedImageKey, { selectedImageKey }),
      },
    });
  };

  const onTopBlockImagePress = (selectedImageKey: string) => {
    batchNavigation({ selectedImageKey });
  };

  const onTopBlockPress = () => {
    const batchNotLinked = links?.length === 0;
    const singleLinkedBatch = links?.length === 1;
    const ncFileLinked = ncFileStatus === ncFileStatuses.Linked;

    const tenantWithERP = withERP(isWithERP);

    if (ncFileUnknown || ncFileIgnored || ncFileChange || ncFileSetup) {
      Popup({
        Component: (p: any) => (
          <NCFileType
            {...{ ...p, selectedItem: machine, productionBatchId: batch.productionBatchId, ncFileId: batch?.ncFileId, ncFileName: batch?.ncFileName }}
          />
        ),
        ...getLinkingModalConfig(scale, fullWidth),
      });

      return;
    }

    if (batchNotLinked) {
      Popup({
        Component: (p: any) => (
          <ProductionInformation
            {...{
              ...p,
              selectedItem: machine,
              productionBatchId: batch?.productionBatchId,
              selectedNCFileId: batch?.ncFileId,
            }}
          />
        ),
        ...getLinkingModalConfig(scale, fullWidth),
      });
      return;
    }

    if (singleLinkedBatch && ncFileLinked && tenantWithERP) {
      navigateToMainDetailsScreen({
        mainScreen: JobDetailsScreenName,
        subScreen: JobDetailsScreen.JOBS,
        levelSubScreen: JobLevelSubScreen.GENERAL,
        params: {
          perspective: Perspective.JOBS,
          productionOperationId: links[0]?.productionOperationId,
        },
      });
    } else {
      batchNavigation({ selectedImageKey: null });
    }
  };

  return {
    onTopBlockImagePress,
    onTopBlockPress,
    variant,
    withoutPressHandlingOnTopBlock,
    ncFileName,
    shouldDisplayNCFileNameForLinkedUnknownNCFileBlock,
    isBatchAvailable,
    batchLoaded,
  };
};

export { useManageTopBlock };
